import { PageTitle } from "~/common/components/PageTitle/v2/PageTitle";
import { ACTIONS, useGearboxSALogic } from "~/GearboxSelectionAssistant/useGearboxSALogic";

import classes from "./GearboxSelectionAssistant.page.module.less";
import { CharacteristicsFilter } from "~/GearboxSelectionAssistant/components/CharacteristicsFilter/CharacteristicsFilter";
import { DimensionsFilter } from "~/GearboxSelectionAssistant/components/DimensionsFilter/DimensionsFilter";
import { Trans, useTranslation } from "react-i18next";
import { useFetchGearboxSAData } from "~/GearboxSelectionAssistant/useFetchGearboxSAData";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { Button } from "~/common/components/Button/Button";
import { Link } from "~/common/components/Link";
import { IconType } from "~/common/components/icon/IconType";
import { Icon } from "~/common/components/icon/Icon";
import { GearboxSelectionAssistantFilterModal } from "~/GearboxSelectionAssistant/components/GearboxSelectionAssistantFilterModal";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { ResultList } from "~/GearboxSelectionAssistant/components/ResultsSection/components/ResultList/ResultList";
import { SortingPicker } from "~/Search/components/common/SortingPicker";
import { SearchDisplayMode, SortDirection } from "~/Search/models";
import { useEffect, useState } from "react";
import { DisplayModeButtonList } from "~/Search/components/common/SearchResultsDisplayMode/SearchResultsDisplayMode";
import { GSATabularView } from "~/GearboxSelectionAssistant/components/ResultsSection/components/GSATabularView/GSATabularView";
import { NoResults } from "~/GearboxSelectionAssistant/components/ResultsSection/components/NoResults";
import { type DimensionKeys } from "./components/DimensionsFilter/types";
import { type CharacteristicKeys } from "./components/CharacteristicsFilter/types";
import { generateUrlFromGearboxFilterValues } from "~/Search/utils/historyManipulation";

export const GearboxSelectionAssistantPage = () => {
  const [state, dispatch] = useGearboxSALogic();
  useFetchGearboxSAData(state, dispatch);
  const { t } = useTranslation();

  const { error, fetching, initialised } = state;

  const isMinTablet = useIsMinTabletSize();
  const { val: showModal, setTrue: openModal, setFalse: closeModal } = useBooleanState(false);

  const [displayMode, setDisplayMode] = useState(SearchDisplayMode.LIST);

  const applyFiltersBasedOnUrl = () => {
    const urlParams = new URLSearchParams(window.location.search.toString());
    const entries = urlParams.entries();
    const params: Record<string, string> = {};
    for (const [key, value] of entries) {
      params[key] = value;
    }

    const filterValues: { [key: string]: string[] | [number, number] } = {};

    for (const key in params) {
      if (key.includes("dvaluemin") || key.includes("dvaluemax")) {
        const propName = key.replace("dvaluemin", "").replace("dvaluemax", "");
        const minProp = `dvaluemin${propName}`;
        const maxProp = `dvaluemax${propName}`;

        const propValue =
          params[minProp] !== undefined ? parseInt(params[minProp]) : state.dimensionDefinitions[propName].min;
        const maxPropValue =
          params[maxProp] !== undefined ? parseInt(params[maxProp]) : state.dimensionDefinitions[propName].max;

        if (!isNaN(propValue) || !isNaN(maxPropValue)) {
          filterValues[`dimension-${propName}`] = [propValue, maxPropValue];
        }
      } else {
        filterValues[`characteristic-${key}`] = urlParams.getAll(key);
      }
    }

    Object.keys(filterValues).forEach((parameter) => {
      const value = filterValues[parameter];
      const parameterName = parameter.split("-")[1];
      if (parameter.split("-")[0] === "dimension") {
        dispatch(ACTIONS.updateDimension(parameterName as DimensionKeys, value as [number, number]));
      } else {
        dispatch(ACTIONS.updateCharacteristic(parameterName as CharacteristicKeys, value as [string]));
      }
    });
  };

  useEffect(() => {
    if (initialised) {
      applyFiltersBasedOnUrl();
    }
  }, [initialised]);

  useEffect(() => {
    if (initialised) {
      window.history.replaceState({}, "", generateUrlFromGearboxFilterValues(state));
    }
  }, [state.characteristicValues, state.dimensionValues]);

  let contents;
  if (isMinTablet) {
    contents = (
      <>
        {initialised && (
          <CharacteristicsFilter
            className={classes.characteristicFilter}
            resultCount={state.resultCount}
            onChange={(code, value) => dispatch(ACTIONS.updateCharacteristic(code, value))}
            onReset={() => dispatch(ACTIONS.resetAll())}
            definitions={state.characteristicDefinitions}
            values={state.characteristicValues}
          />
        )}
        {initialised && (
          <DimensionsFilter
            className={classes.dimensionFilter}
            onChange={(code, value) => dispatch(ACTIONS.updateDimension(code, value))}
            definitions={state.dimensionDefinitions}
            values={state.dimensionValues}
          />
        )}
      </>
    );
  } else {
    if (!initialised) {
      contents = null;
    }
    contents = (
      <div>
        <Button fullWidth={true} startIcon={<Icon type={IconType.Filter} />} onClick={openModal}>
          {t("page.sa.gearbox.btn.showFilters")}
        </Button>
        <div className={classes.mobileCharacteristicTitle}>
          <Trans i18nKey="page.sa.gearbox.characteristics.description.mobile">
            <Link>Search via competitor’s designation</Link> to find a suitable precision strain wave gear
          </Trans>
        </div>
        {showModal && (
          <GearboxSelectionAssistantFilterModal
            onClose={closeModal}
            initialData={state}
            onSubmit={(data) => dispatch(ACTIONS.updateFromModal(data))}
          />
        )}
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <PageTitle className={classes.title}>{t("page.sa.gearbox.title")}</PageTitle>

      {contents}

      {fetching !== "searching" && !error && (
        <div className={classes.results}>
          <div className={classes.resultsTitle}>
            {t("page.sa.gearbox.results.title", { count: state.totalAmountOfResults })}
          </div>

          {state.totalAmountOfResults > 0 && (
            <>
              <div className={classes.sortingSection}>
                <DisplayModeButtonList
                  className={classes.displayModeList}
                  displayMode={displayMode}
                  setDisplayMode={setDisplayMode}
                />
                {displayMode === SearchDisplayMode.LIST && (
                  <SortingPicker
                    className={classes.sortingPicker}
                    options={state.sortOptions}
                    selectedCode={state.selectedSort}
                    selectedDirection={state.selectedSortDirection ?? null}
                    onSelect={(newCode) => dispatch(ACTIONS.updateSort(newCode))}
                    onAscDesc={(newDirection) => dispatch(ACTIONS.updateSort(state.selectedSort, newDirection))}
                    useTranslationKeyForLabel={true}
                  />
                )}
              </div>

              {displayMode === SearchDisplayMode.LIST && <ResultList results={state.results} />}

              {displayMode === SearchDisplayMode.TABULAR && (
                <GSATabularView
                  resultCount={state.resultCount}
                  results={state.results}
                  sort={{ id: state.selectedSort, desc: state.selectedSortDirection === SortDirection.DESC }}
                  onSortChange={(newSort) =>
                    dispatch(ACTIONS.updateSort(newSort.id, newSort.desc ? SortDirection.DESC : SortDirection.ASC))
                  }
                />
              )}

              {state.currentPage + 1 < state.totalAmountOfPages && (
                <Button className={classes.moreResults} onClick={() => dispatch(ACTIONS.loadMore())}>
                  {t("page.sa.gearbox.btn.loadMore")}
                </Button>
              )}
            </>
          )}
          {state.totalAmountOfResults === 0 && <NoResults />}
        </div>
      )}

      {fetching && (
        <div className={!initialised ? classes.fetchingUninitialised : classes.fetchingInitialised}>
          {t("spinner.text")}
        </div>
      )}
      {error && (
        <div className={!initialised ? classes.errorUninitialised : classes.errorInitialised}>
          {t("notificationsPage.error")}: {error}
        </div>
      )}
    </div>
  );
};

export const createGearboxSelectionAssistantPage = () => {
  return <GearboxSelectionAssistantPage />;
};
